import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Eye, Save, Send, Sparkles, TestTube2, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Card, ErrorBanner, Notice, PageHeader, Spinner, StatusBadge, useLoad } from "../../components/kit";
import { AiDraftDialog, CampaignReport, SendDialog, TestDialog } from "../../components/CampaignTools";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Switch } from "../../components/ui/switch";
import { Textarea } from "../../components/ui/textarea";
import { api, errMsg } from "../../lib/api";
import { useAuth } from "../../lib/auth";

const FIELDS = ["name", "audience_id", "from", "reply_to", "subject", "subject_b", "preview_text", "html", "text", "track_opens", "track_clicks", "split_test"];
const SAMPLE = { first_name: "Alex", last_name: "Example", email: "alex@example.com", unsubscribe_url: "#" };
const sample = (s) => (s || "").replace(/\{\{\s*([a-zA-Z_][a-zA-Z0-9_]*)\s*\}\}/g, (m, k) => SAMPLE[k] ?? m);
const VARS = ["{{first_name}}", "{{last_name}}", "{{email}}", "{{unsubscribe_url}}"];

function Field({ label, hint, children }) {
  return <div className="space-y-1.5"><Label className="text-zinc-300">{label}</Label>{children}{hint && <p className="text-xs text-zinc-500">{hint}</p>}</div>;
}

function Basics({ f, set, audiences, domains }) {
  return (
    <Card testId="campaign-basics">
      <div className="eyebrow mb-5">1 · Basics</div>
      <div className="grid gap-5 sm:grid-cols-2">
        <Field label="Campaign name"><Input value={f.name} onChange={(e) => set({ name: e.target.value })} data-testid="campaign-name-input" /></Field>
        <Field label="Audience" hint={audiences.length ? null : <>No audiences yet. <Link to="/app/audiences" className="text-purple-300">Create one</Link>.</>}>
          <Select value={f.audience_id || ""} onValueChange={(v) => set({ audience_id: v })}>
            <SelectTrigger data-testid="campaign-audience-select"><SelectValue placeholder="Choose an audience" /></SelectTrigger>
            <SelectContent>{audiences.map((a) => <SelectItem key={a.id} value={a.id} data-testid={`campaign-audience-${a.id}`}>{a.name} · {a.contacts}</SelectItem>)}</SelectContent>
          </Select></Field>
        <Field label="From" hint={domains.length ? `Verified domains: ${domains.join(", ")}` : "No verified domain yet. Verify one under Domains first."}>
          <Input value={f.from} onChange={(e) => set({ from: e.target.value })} placeholder={`Your Brand <news@${domains[0] || "yourdomain.com"}>`} data-testid="campaign-from-input" /></Field>
        <Field label="Reply-to (optional)"><Input value={f.reply_to || ""} onChange={(e) => set({ reply_to: e.target.value })} placeholder="hello@yourdomain.com" data-testid="campaign-replyto-input" /></Field>
      </div>
    </Card>
  );
}

function SubjectCard({ f, set }) {
  const st = f.split_test || { enabled: false, test_percent: 20, wait_hours: 4 };
  const setSt = (p) => set({ split_test: { ...st, ...p } });
  return (
    <Card testId="campaign-subject">
      <div className="eyebrow mb-5">2 · Subject</div>
      <div className="space-y-5">
        <Field label={st.enabled ? "Subject A" : "Subject"}><Input value={f.subject} onChange={(e) => set({ subject: e.target.value })} data-testid="campaign-subject-input" /></Field>
        <Field label="Preview text" hint="The grey line shown after the subject in most inboxes."><Input value={f.preview_text} onChange={(e) => set({ preview_text: e.target.value })} data-testid="campaign-preview-input" /></Field>
        <label className="flex items-center justify-between gap-3 rounded-xl border border-white/10 bg-white/[0.02] p-4">
          <span><span className="block text-sm text-white">Split test two subject lines</span><span className="block text-xs text-zinc-500">A test group gets A or B; after the wait, the subject with more opens goes to everyone else.</span></span>
          <Switch checked={st.enabled} onCheckedChange={(v) => setSt({ enabled: v })} data-testid="campaign-split-switch" />
        </label>
        {st.enabled && (
          <div className="grid gap-4 sm:grid-cols-3">
            <div className="sm:col-span-3"><Field label="Subject B"><Input value={f.subject_b} onChange={(e) => set({ subject_b: e.target.value })} data-testid="campaign-subject-b-input" /></Field></div>
            <Field label="Test group %" hint="10–50% of the audience"><Input type="number" min={10} max={50} value={st.test_percent} onChange={(e) => setSt({ test_percent: Number(e.target.value) })} data-testid="campaign-split-percent" /></Field>
            <Field label="Wait (hours)" hint="1–48 hours before picking"><Input type="number" min={1} max={48} value={st.wait_hours} onChange={(e) => setSt({ wait_hours: Number(e.target.value) })} data-testid="campaign-split-wait" /></Field>
          </div>)}
      </div>
    </Card>
  );
}

function ContentCard({ f, set, onAi }) {
  const [preview, setPreview] = useState(false);
  return (
    <Card testId="campaign-content">
      <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
        <div className="eyebrow">3 · Content</div>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={onAi} className="border-purple-400/30 bg-purple-500/10 text-purple-100 hover:bg-purple-500/20" data-testid="campaign-ai-button"><Sparkles className="mr-1 h-4 w-4" />Write with AI</Button>
          <Button size="sm" variant="outline" onClick={() => setPreview(!preview)} className="border-white/10 bg-transparent" data-testid="campaign-preview-toggle"><Eye className="mr-1 h-4 w-4" />{preview ? "Edit" : "Preview"}</Button>
        </div>
      </div>
      <p className="mb-4 text-xs text-zinc-500">Personalise with {VARS.map((v) => <code key={v} className="mx-0.5 rounded bg-white/5 px-1 text-purple-200">{v}</code>)} and any CSV column. An unsubscribe footer is added automatically if you don't place {"{{unsubscribe_url}}"} yourself.</p>
      {preview ? <iframe title="Campaign preview" sandbox="" srcDoc={sample(f.html) || `<pre>${sample(f.text).replace(/</g, "&lt;")}</pre>`} className="h-[520px] w-full rounded-lg border border-white/10 bg-white" data-testid="campaign-preview-frame" /> : (
        <div className="space-y-4">
          <Field label="HTML"><Textarea rows={14} value={f.html} onChange={(e) => set({ html: e.target.value })} className="font-mono text-xs" data-testid="campaign-html-input" /></Field>
          <Field label="Plain text (recommended)"><Textarea rows={6} value={f.text} onChange={(e) => set({ text: e.target.value })} className="font-mono text-xs" data-testid="campaign-text-input" /></Field>
        </div>)}
    </Card>
  );
}

function TrackingCard({ f, set }) {
  const forced = f.split_test?.enabled;
  return (
    <Card testId="campaign-tracking">
      <div className="eyebrow mb-5">4 · Tracking</div>
      <div className="grid gap-4 sm:grid-cols-2">
        {[["track_opens", "Open tracking", forced ? "Always on during a split test (opens pick the winner)." : "Off unless you turn it on."], ["track_clicks", "Click tracking", "Counts which links people click."]].map(([k, label, hint]) => (
          <label key={k} className="flex items-start justify-between gap-3"><span><span className="block text-sm text-white">{label}</span><span className="block text-xs text-zinc-500">{hint}</span></span>
            <Switch checked={!!f[k] || (k === "track_opens" && forced)} disabled={k === "track_opens" && forced} onCheckedChange={(v) => set({ [k]: v })} data-testid={`campaign-${k}-switch`} /></label>))}
      </div>
    </Card>
  );
}

function Editor({ c, reload }) {
  const nav = useNavigate();
  const [f, setF] = useState(() => Object.fromEntries(FIELDS.map((k) => [k, c[k] ?? (k === "reply_to" ? null : "")])));
  const [dirty, setDirty] = useState(false);
  const [dialog, setDialog] = useState(null);
  const set = (p) => { setF((x) => ({ ...x, ...p })); setDirty(true); };
  const aud = useLoad(() => api.get("/app/audiences").then((r) => r.data), []);
  const dom = useLoad(() => api.get("/app/domains").then((r) => r.data.domains.filter((d) => d.status === "verified").map((d) => d.name)), []);
  const save = async (quiet) => {
    const { data } = await api.put(`/app/campaigns/${c.id}`, { ...f, reply_to: f.reply_to || null });
    setDirty(false); if (!quiet) toast.success("Campaign saved");
    return data;
  };
  const trySave = async () => { try { await save(); } catch (e) { toast.error(errMsg(e)); } };
  const open = async (name) => { try { if (dirty) await save(true); setDialog(name); } catch (e) { toast.error(errMsg(e)); } };
  const remove = async () => { if (window.confirm("Delete this campaign?")) { try { await api.delete(`/app/campaigns/${c.id}`); nav("/app/campaigns"); } catch (e) { toast.error(errMsg(e)); } } };
  useEffect(() => {
    if (!dirty) return undefined;
    const warn = (e) => { e.preventDefault(); e.returnValue = ""; };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [dirty]);
  return (
    <div className="space-y-6">
      <div className="sticky top-2 z-20 flex flex-wrap items-center gap-2 rounded-2xl border border-white/10 bg-[#0B0A0F]/85 p-3 backdrop-blur-xl" data-testid="campaign-actions">
        <Button variant="outline" onClick={trySave} disabled={!dirty} className="border-white/10 bg-transparent" data-testid="campaign-save-button"><Save className="mr-1 h-4 w-4" />{dirty ? "Save" : "Saved"}</Button>
        <Button variant="outline" onClick={() => open("test")} className="border-white/10 bg-transparent" data-testid="campaign-test-button"><TestTube2 className="mr-1 h-4 w-4" />Send test</Button>
        <Button onClick={() => open("send")} data-testid="campaign-send-button"><Send className="mr-1 h-4 w-4" />Send or schedule</Button>
        <Button variant="ghost" onClick={remove} className="ml-auto text-red-300 hover:bg-red-500/10" data-testid="campaign-delete-button"><Trash2 className="h-4 w-4" /></Button>
      </div>
      <ErrorBanner error={aud.error || dom.error} />
      <Basics f={f} set={set} audiences={aud.data || []} domains={dom.data || []} />
      <SubjectCard f={f} set={set} />
      <ContentCard f={f} set={set} onAi={() => setDialog("ai")} />
      <TrackingCard f={f} set={set} />
      <AiDraftDialog open={dialog === "ai"} onClose={() => setDialog(null)} onApply={(d) => set({ subject: d.subject, subject_b: d.subject_b || f.subject_b, preview_text: d.preview_text, html: d.html, text: d.text })} />
      <TestDialog open={dialog === "test"} onClose={() => setDialog(null)} id={c.id} />
      <SendDialog open={dialog === "send"} onClose={() => setDialog(null)} c={{ ...c, ...f }} audiences={aud.data || []} onDone={reload} />
    </div>
  );
}

export default function CampaignPage() {
  const { id } = useParams();
  const { can } = useAuth();
  const { data, error, loading, reload } = useLoad(() => api.get(`/app/campaigns/${id}`).then((r) => r.data), [id]);
  return (
    <div>
      <Link to="/app/campaigns" className="mb-4 inline-flex items-center gap-1 text-sm text-zinc-400 hover:text-white" data-testid="campaign-back-link"><ArrowLeft className="h-4 w-4" />Campaigns</Link>
      <PageHeader eyebrow="Campaign" title={data?.name || "Campaign"} actions={data && <StatusBadge status={data.status} testId="campaign-status" />} />
      <ErrorBanner error={error} />
      {loading && !data ? <Spinner /> : data && (data.status === "draft" && can("developer") ? <Editor key={data.updated_at} c={data} reload={reload} />
        : data.status === "draft" ? <Notice title="Draft">Only developers and above can edit campaigns.</Notice> : <CampaignReport c={data} reload={reload} />)}
    </div>
  );
}
